import { DynamicObjectInterface } from '../../sdk/type';

/**
 * Resolve whether the Front/Side Profile Image Section should be shown for a
 * given participant.
 *
 * The flag `campaignIsProfileImageSectionShown` historically lived only at the
 * campaign level. For MULTI-mode (MY multi-sub-challenge) campaigns it is now
 * configured PER sub-challenge on `activities.subChallenges[i]`, and the
 * campaign-level toggle is hidden in the admin config.
 *
 * Resolution rules:
 *  - MULTI campaign with a resolvable sub-challenge  -> that sub-challenge's flag
 *  - MULTI campaign but sub-challenge not found       -> campaign-level flag (safe fallback)
 *  - SINGLE / legacy campaign                         -> campaign-level flag
 *
 * `configJson` is the campaign config object (as delivered on
 * `data.campaign.configJson` / `campaign.configJson`). `subChallengeId` is the
 * participant's assigned sub-challenge id (may be undefined for SINGLE campaigns).
 */
export const resolveProfileImageSectionShown = (
  configJson: DynamicObjectInterface | null | undefined,
  subChallengeId?: string | null,
): boolean => {
  if (!configJson) return false;

  const campaignLevel = Number(configJson.campaignIsProfileImageSectionShown) === 1;

  const isMulti = configJson.campaignMode === 'MULTI';
  const subChallenges = configJson.activities?.subChallenges as DynamicObjectInterface[] | undefined;

  if (isMulti && Array.isArray(subChallenges) && subChallenges.length > 0) {
    const sc = subChallengeId
      ? subChallenges.find(s => s.subChallengeId === subChallengeId)
      : undefined;
    // Found the participant's sub-challenge -> use its own flag. Otherwise fall
    // back to the campaign-level value so behaviour degrades safely.
    if (sc) return Number(sc.campaignIsProfileImageSectionShown) === 1;
    return campaignLevel;
  }

  return campaignLevel;
};

// Locate the participant's sub-challenge config object from campaign configJson.
// Returns undefined for SINGLE / legacy campaigns or when it can't be resolved.
export const resolveSubChallenge = (
  configJson: DynamicObjectInterface | null | undefined,
  subChallengeId?: string | null,
): DynamicObjectInterface | undefined => {
  if (!configJson || configJson.campaignMode !== 'MULTI' || !subChallengeId) return undefined;
  const subChallenges = configJson.activities?.subChallenges as DynamicObjectInterface[] | undefined;
  if (!Array.isArray(subChallenges)) return undefined;
  return subChallenges.find(s => s.subChallengeId === subChallengeId);
};

// Assessment indicators (rules[4].subKpiCode) for a participant: the sub-challenge's
// own list in MULTI mode, otherwise the legacy campaign-level participant list.
export const resolveSubKpiCode = (
  configJson: DynamicObjectInterface | null | undefined,
  subChallengeId?: string | null,
): DynamicObjectInterface[] => {
  const sc = resolveSubChallenge(configJson, subChallengeId);
  if (sc?.rules?.[4]?.subKpiCode) return sc.rules[4].subKpiCode;
  return configJson?.activities?.participant?.rules?.[4]?.subKpiCode ?? [];
};

// Assessment file uploaders for a participant: the sub-challenge's own list in
// MULTI mode, otherwise the legacy campaign-level participant list.
export const resolveFileUploader = (
  configJson: DynamicObjectInterface | null | undefined,
  subChallengeId?: string | null,
): DynamicObjectInterface[] => {
  const sc = resolveSubChallenge(configJson, subChallengeId);
  if (Array.isArray(sc?.fileUploader)) return sc.fileUploader;
  return configJson?.activities?.participant?.fileUploader ?? [];
};

// Configured check-in days for a participant's sub-challenge (MULTI only).
export const resolveCheckInDays = (
  configJson: DynamicObjectInterface | null | undefined,
  subChallengeId?: string | null,
): number[] => {
  const sc = resolveSubChallenge(configJson, subChallengeId);
  return Array.from(
    new Set(((sc?.checkIn?.days ?? []) as (number | string)[]).map(d => Number(d)).filter(d => !isNaN(d) && d > 0)),
  ).sort((a, b) => a - b);
};

// Filter assessment indicators to those relevant for the participant's chosen
// test device. When no indicator is device-tagged, returns the list unchanged.
// Otherwise keeps untagged indicators plus those tagged to `deviceCode`.
export const filterSubKpiByDevice = (
  subKpiCode: DynamicObjectInterface[],
  deviceCode?: string | null,
): DynamicObjectInterface[] => {
  const list = subKpiCode ?? [];
  const hasDeviceTagged = list.some(i => !!i.testDevice);
  if (!hasDeviceTagged) return list;
  return list.filter(i => !i.testDevice || i.testDevice === deviceCode);
};

// Display name of the test device the participant used, resolved from the device
// code stored on weightIn/weightOut.machine against the sub-challenge's testDevices.
// Falls back to the raw code, or '' when no device was recorded.
export const resolveTestDeviceName = (
  configJson: DynamicObjectInterface | null | undefined,
  subChallengeId: string | null | undefined,
  deviceCode?: string | null,
): string => {
  if (!deviceCode) return '';
  const sc = resolveSubChallenge(configJson, subChallengeId);
  const devices = (sc?.testDevices ?? []) as DynamicObjectInterface[];
  return devices.find(d => d.code === deviceCode)?.name ?? deviceCode;
};

/**
 * Resolve the passing threshold for an assessment indicator whose standard varies
 * by participant gender + age group (e.g. Grip Strength), from its
 * `genderAgeThresholds` matrix. Returns null when the indicator has no matrix, or
 * the participant's gender/ageGroup is missing/unmapped — callers then fall back to
 * the flat targetFrom / diff behaviour.
 *
 * Mirrors the BFF `resolveGenderAgeThreshold` (participantService.js) so the
 * front-end display matches the backend winning calculation.
 */
export const resolveGenderAgeThreshold = (
  subKpi: DynamicObjectInterface | null | undefined,
  gender?: string | null,
  ageGroup?: string | null,
): number | null => {
  const matrix = subKpi?.genderAgeThresholds;
  if (!matrix || typeof matrix !== 'object') return null;
  if (!gender || !ageGroup) return null;
  const byGender = matrix[String(gender).toUpperCase()];
  if (!byGender || typeof byGender !== 'object') return null;
  const raw = byGender[ageGroup];
  if (raw === undefined || raw === null || raw === '') return null;
  const threshold = parseFloat(raw);
  return Number.isNaN(threshold) ? null : threshold;
};
